
import Vue from "vue";
import ImageView from "@/components/atoms/ImageView.vue";
import { Novel } from "@/lib/models";
import { workStatus } from "@/lib/novelSettings";
import { NovelSetting } from "@/lib/models/novel";

interface Data {
  isHovered: boolean;
  showGenrePopup: boolean;
}

interface Methods {
  submit: (event: Event) => void;
  hideGenrePopup: () => void;
  isGenreOrCategoryUnset: () => boolean;
}

interface Computed {
  novel: Novel | null;
  imageKey?: string | null;
  currentWorkStatus: NovelSetting;
  workStatusButtonStyle: { backgroundColor: string };
  hoverStyle: { backgroundColor: string };
  isGenreSettingPopupDisplayedForNovel: boolean;
}

interface Props {
  novelId: string;
  expand: boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { ImageView },

  props: {
    novelId: String,
    expand: Boolean,
  },

  data() {
    return {
      isHovered: false,
      showGenrePopup: false,
    };
  },

  mounted() {
    // ジャンルまたはカテゴリが未設定かつ、ジャンル設定ポップアップが表示されていない場合
    if (this.isGenreOrCategoryUnset() && !this.isGenreSettingPopupDisplayedForNovel) {
      this.showGenrePopup = true;
    }
  },

  computed: {
    novel() {
      const { novelId } = this;
      const { getters } = this.$store;
      return getters["novelModule/novel"](novelId);
    },
    imageKey() {
      if (!this.novel) {
        return null;
      }

      if (this.novel && this.novel.image && this.novel.image.startsWith("file:")) {
        return `file:novels/${this.novelId}/cover.jpg`;
      }

      return this.novel.image;
    },
    currentWorkStatus() {
      const { novel } = this;
      return workStatus.find((status) => status.id === novel.workStatus) || workStatus[0];
    },
    workStatusButtonStyle() {
      const colors = {
        inProgress: "var(--color-status-in-progress)",
        completed: "var(--color-status-completed)",
        pending: "var(--color-status-pending)",
        suspend: "var(--color-status-suspend)",
      };
      return {
        backgroundColor: colors[this.currentWorkStatus.id] || "var(--color-status-in-progress)",
      };
    },
    hoverStyle() {
      // ホバー時は少し暗くする（filter: brightness(0.85)相当の色）
      const hoverColors = {
        inProgress: "var(--color-status-in-progress-hover)",
        completed: "var(--color-status-completed-hover)",
        pending: "var(--color-status-pending-hover)",
        suspend: "var(--color-status-suspend-hover)",
      };
      return {
        backgroundColor: hoverColors[this.currentWorkStatus.id] || hoverColors.inProgress,
      };
    },
    isGenreSettingPopupDisplayedForNovel() {
      const { $store, novelId } = this;
      return $store.getters["generalModule/isGenreSettingPopupDisplayedForNovel"](novelId);
    },
  },

  methods: {
    submit(event: Event) {
      const { novelId } = this;

      const target = event.target as HTMLElement;

      // `work-status-button` またはその子要素がクリックされた場合
      if (target.closest(".work-status-button")) {
        const targetRoute = { name: "detailEdit", params: { novelId: this.novelId } } as const;
        // 既に対象ルートの場合は何もしない
        if (
          this.$route.name === targetRoute.name &&
          this.$route.params.novelId === String(targetRoute.params.novelId)
        ) {
          return;
        }
        this.$router.push(targetRoute);
        return;
      }

      // それ以外の場所がクリックされた場合
      const targetRoute = { name: "detail", params: { novelId } } as const;
      if (this.$route.name === targetRoute.name && this.$route.params.novelId === String(targetRoute.params.novelId)) {
        return;
      }
      this.$router.push(targetRoute);
    },
    async hideGenrePopup() {
      this.showGenrePopup = false;
      // 表示中の作品のジャンル設定ポップアップが閉じられたことを記録
      await this.$store.dispatch("generalModule/addDisplayedGenreSettingPopupForNovel", this.novelId);
    },
    isGenreOrCategoryUnset() {
      return !this.novel.genre || !this.novel.category;
    },
  },

  watch: {
    novel: {
      handler(newVal) {
        if (newVal?.genre && newVal?.category) {
          this.showGenrePopup = false;
        }
      },
      deep: true,
    },
  },
});
